import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useEvents } from '../context/EventsContext';
import { containsProfanity } from '../utils/profanityFilter';
import MINECRAFT_COMMANDS from '../data/minecraftCommands';
import ChunkDeletePage from './ChunkDeletePage';
import BlockDeletePage from './BlockDeletePage';
import WouldYouRatherPage from './WouldYouRatherPage';
import EventAuthBar from '../components/EventAuthBar';
import { signalTransitionReady } from '../components/EventTransition';
import { useAppAuth } from '../context/AuthContext';
import { renderMinecraftTextHTML, stripMinecraftCodes } from '../utils/minecraftIcons';
import useTurnstileToken from '../utils/useTurnstileToken';
import VerifyingProgress from '../components/VerifyingProgress';
import './LiveEventPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const DISPLAY_NAME_KEY = 'thetopia_event_displayname';

/* ── Fingerprint (lazy-loaded) ── */
let fpPromise = null;
async function getFingerprint() {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then(async (FP) => {
      const agent = await FP.load();
      const result = await agent.get();
      return result.visitorId;
    });
  }
  return fpPromise;
}

/* ── Username Gate ── */
function UsernameGate({ onSubmit }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 24) {
      setError('Name must be 2-24 characters');
      return;
    }
    if (containsProfanity(trimmed)) {
      setError('Please choose an appropriate name');
      return;
    }
    localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
    onSubmit(trimmed);
  };

  return (
    <div className="le-gate">
      <div className="le-gate-card">
        <h2 className="le-gate-title">Enter Display Name</h2>
        <p className="le-gate-desc">This is how other viewers will see you</p>
        <form onSubmit={handleSubmit} className="le-gate-form">
          <input
            type="text"
            className="le-gate-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder="Your name..."
            maxLength={24}
            autoFocus
          />
          {error && <span className="le-gate-error">{error}</span>}
          <button type="submit" className="le-gate-btn">Join Event</button>
        </form>
      </div>
    </div>
  );
}

/* ── Round Header (timer, round number, state badge) ── */
function RoundHeader({ state }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    // Active round timer
    if (state.roundState === 'active' && state.roundStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.roundStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    // Intermission countdown
    if (state.roundState === 'intermission' && state.intermissionStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.intermissionStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    // Paused - show frozen timer from the state we paused from
    if (state.roundState === 'paused' && state.pausedAt) {
      if (state.prePauseState === 'intermission' && state.intermissionStartedAt) {
        const elapsed = state.pausedAt - state.intermissionStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      } else if (state.roundStartedAt) {
        const elapsed = state.pausedAt - state.roundStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      }
    } else if (state.roundState === 'idle') {
      setTimeLeft(null);
    }
  }, [state.roundStartedAt, state.roundDurationSec, state.pausedElapsed, state.roundState, state.pausedAt, state.intermissionStartedAt, state.intermissionSec, state.prePauseState]);

  const formatTime = (seconds) => {
    if (seconds == null) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isPaused = state.roundState === 'paused';

  const stateInfo = {
    active: {
      label: 'LIVE',
      className: 'le-badge-live',
      hint: 'Submit a command below that you want ran in-game! The most voted command will be executed when the timer runs out.',
    },
    intermission: {
      label: 'INTERMISSION',
      className: 'le-badge-intermission',
      hint: 'The winning command is being executed in-game. The next round starts soon so get your command ready!',
    },
    paused: {
      label: 'PAUSED',
      className: 'le-badge-paused',
      hint: 'The event has been paused. Hang tight, it\'ll resume shortly, maybe? idk.',
    },
    idle: {
      label: 'WAITING',
      className: 'le-badge-idle',
      hint: 'The event hasn\'t started yet. Stay on this page! You\'ll be able to submit commands once the event begins.',
    },
  }[state.roundState] || { label: state.roundState?.toUpperCase(), className: '', hint: '' };

  return (
    <>
      <div className="le-round-header">
        <div className="le-round-info">
          <span className={`le-state-badge ${stateInfo.className}`}>{stateInfo.label}</span>
          {state.roundNumber > 0 && (
            <span className="le-round-number">Round {state.roundNumber}</span>
          )}
        </div>
        <div className={`le-timer${isPaused ? ' le-timer-paused' : ''}`}>{formatTime(timeLeft)}</div>
        <div className="le-connections">
          <span className="le-connections-dot" />
          {state.connections} watching
        </div>
      </div>
      {stateInfo.hint && (
        <div className={`le-status-hint le-status-hint-${state.roundState}`}>
          {stateInfo.hint}
        </div>
      )}
    </>
  );
}

/* ── Command Input with Tab Completion ── */
const CommandInput = React.memo(function CommandInput({ onSubmit, disabled, placeholder }) {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value.startsWith('/') && value.length > 1) {
      const search = value.split(' ')[0].toLowerCase();
      const matches = MINECRAFT_COMMANDS.filter(c =>
        c.name.toLowerCase().startsWith(search)
      ).slice(0, 8);
      setSuggestions(matches);
      setSelectedIdx(0);
    } else {
      setSuggestions([]);
    }
  }, [value]);

  const applySuggestion = (cmd) => {
    const parts = value.split(' ');
    parts[0] = cmd.name;
    setValue(parts.join(' ') + (parts.length === 1 ? ' ' : ''));
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'Tab' || e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        if ((e.key === 'Tab' && !e.shiftKey) || e.key === 'Enter') {
          applySuggestion(suggestions[selectedIdx]);
        } else {
          setSelectedIdx((prev) => (prev + 1) % suggestions.length);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Escape') {
        setSuggestions([]);
        return;
      }
    }

    // Block Enter from submitting — submission is via button only
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue('');
    setSuggestions([]);
  };

  return (
    <div className="le-input-wrapper">
      <div className="le-input-container">
        <input
          ref={inputRef}
          type="text"
          className="le-command-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || '/command ...'}
          maxLength={200}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className="le-submit-btn"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
        >
          Submit
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="le-suggestions">
          {suggestions.map((cmd, i) => (
            <button
              key={cmd.name}
              className={`le-suggestion ${i === selectedIdx ? 'le-suggestion-active' : ''}`}
              onClick={() => applySuggestion(cmd)}
            >
              <span className="le-suggestion-name">{cmd.name}</span>
              <span className="le-suggestion-hint">{cmd.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/* ── Command List ── */
const CommandList = React.memo(function CommandList({ commands, yourVote, onVote, disabled }) {
  if (commands.length === 0) {
    return (
      <div className="le-commands-empty">
        No commands submitted yet. Be the first!
      </div>
    );
  }

  return (
    <div className="le-commands">
      {commands.map((cmd, i) => {
        const isVoted = cmd.id === yourVote;
        const isWinner = i === 0 && cmd.votes > 0;

        return (
          <div
            key={cmd.id}
            className={`le-command ${isWinner ? 'le-command-leading' : ''} ${isVoted ? 'le-command-voted' : ''}`}
          >
            <button
              className={`le-vote-btn ${isVoted ? 'le-vote-btn-active' : ''}`}
              onClick={() => onVote(cmd.id)}
              disabled={disabled}
            >
              <span className="le-vote-arrow">{isVoted ? '\u25B2' : '\u25B3'}</span>
              <span className="le-vote-count">{cmd.votes}</span>
            </button>
            <div className="le-command-body">
              <span className="le-command-text">{cmd.command}</span>
              <span className="le-command-author">by {cmd.displayName}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
});

/* ── Allowed Commands Sidebar ── */
const AllowedCommandsSidebar = React.memo(function AllowedCommandsSidebar({ commands }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <aside className="le-sidebar">
      <div className="le-sidebar-card">
        <button
          className="le-sidebar-header"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="le-sidebar-title">Allowed Commands</span>
          <span className="le-sidebar-count">{commands.length}</span>
          <span className={`le-sidebar-chevron ${expanded ? 'le-sidebar-chevron-open' : ''}`}>&#9662;</span>
        </button>
        <div className={`le-sidebar-list ${expanded ? 'le-sidebar-list-open' : ''}`}>
          {commands.map(cmd => (
            <span key={cmd} className="le-sidebar-cmd">{cmd}</span>
          ))}
        </div>
      </div>
    </aside>
  );
});

/* ── Round History ── */
const RoundHistory = React.memo(function RoundHistory({ history }) {
  if (!history || history.length === 0) return null;

  return (
    <div className="le-history">
      <h3 className="le-history-title">Past Rounds</h3>
      <div className="le-history-list">
        {[...history].reverse().map((entry) => (
          <div key={entry.roundNumber} className="le-history-item">
            <span className="le-history-round">R{entry.roundNumber}</span>
            <span className="le-history-command">{entry.winner.command}</span>
            <span className="le-history-meta">
              by {entry.winner.displayName} ({entry.winner.votes} vote{entry.winner.votes !== 1 ? 's' : ''})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

/* ── Main Live Event Page ── */
export default function LiveEventPage() {
  const { slug } = useParams();
  const { events, loading: eventsLoading } = useEvents();
  const { isSignedIn, minecraftUsername, hasRole, getToken } = useAppAuth();
  const { token: sessionToken, error: turnstileError, retry: retryTurnstile } = useTurnstileToken();

  const [displayName, setDisplayName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || '');

  // Auto-set display name from Minecraft account when signed in
  useEffect(() => {
    if (isSignedIn && minecraftUsername && displayName !== minecraftUsername) {
      localStorage.setItem(DISPLAY_NAME_KEY, minecraftUsername);
      setDisplayName(minecraftUsername);
    }
  }, [isSignedIn, minecraftUsername, displayName]);
  const [fingerprint, setFingerprint] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [wsError, setWsError] = useState(null);
  const [lastError, setLastError] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Find the event by slug (public events first, admin fallback for hidden events)
  const [adminEvent, setAdminEvent] = useState(null);
  const publicEvent = useMemo(() => {
    if (!events) return undefined;
    return events.find(e => e.slug === slug);
  }, [events, slug]);

  useEffect(() => {
    if (publicEvent || eventsLoading || !slug || adminEvent) return;
    if (!hasRole || !hasRole('admin')) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch('/api/admin/events', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const found = (data.events || []).find(e => e.slug === slug);
          if (found) setAdminEvent(found);
        }
      } catch {}
    })();
  }, [publicEvent, eventsLoading, slug, hasRole, getToken, adminEvent]);

  const event = publicEvent || adminEvent;

  // Update tab title with event name
  useEffect(() => {
    if (event?.name) {
      document.title = `${stripMinecraftCodes(event.name)} - TheTopia (Minecraft Events)`;
    }
  }, [event?.name]);

  // Signal transition ready if showing username gate (no WS to wait for)
  useEffect(() => {
    if (!displayName) signalTransitionReady();
  }, [displayName]);

  // Load fingerprint
  useEffect(() => {
    getFingerprint().then(setFingerprint).catch(() => {
      setFingerprint(`anon-${Math.random().toString(36).slice(2, 10)}`);
    });
  }, []);

  // WebSocket connection
  // Skip WS for addon events — they manage their own connections
  const isAddonEvent = event?.webAddon === 'block-delete' || event?.webAddon === 'chunk-delete' || event?.webAddon === 'would-you-rather';

  const connect = useCallback(() => {
    if (!displayName || !fingerprint || !slug || !sessionToken || !event || isAddonEvent) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${protocol}://${PARTYKIT_HOST}/party/${slug}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsError(null);
      signalTransitionReady();
      ws.send(JSON.stringify({
        type: 'join',
        displayName,
        fingerprint,
        token: sessionToken,
      }));
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'state') {
          setRoomState(msg);
        } else if (msg.type === 'update') {
          setRoomState(prev => {
            if (!prev) return null;
            if (msg.delta) {
              // New server: explicit fields only
              return {
                ...prev,
                ...(msg.commands !== undefined && { commands: msg.commands }),
                ...(msg.connections !== undefined && { connections: msg.connections }),
              };
            }
            // Old server fallback: full merge
            const { type, ...fields } = msg;
            return { ...prev, ...fields };
          });
        } else if (msg.type === 'vote-ack') {
          setRoomState(prev => prev ? { ...prev, yourVote: msg.yourVote } : null);
        } else if (msg.type === 'viewers') {
          setRoomState(prev => prev ? { ...prev, connections: msg.connections } : null);
        } else if (msg.type === 'error') {
          setLastError(msg.message);
          setTimeout(() => setLastError(null), 4000);
        }
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      // Reconnect after 2 seconds
      reconnectRef.current = setTimeout(() => connect(), 2000);
    };

    ws.onerror = () => {
      setWsError('Connection lost. Reconnecting...');
    };
  }, [displayName, fingerprint, slug, sessionToken, event, isAddonEvent]);

  useEffect(() => {
    if (displayName && fingerprint && sessionToken) {
      connect();
    }
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect, displayName, fingerprint]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const handleSubmitCommand = useCallback((command) => {
    sendMessage({ type: 'submit-command', command, fingerprint });
  }, [sendMessage, fingerprint]);

  const handleVote = useCallback((commandId) => {
    sendMessage({ type: 'vote', commandId, fingerprint });
  }, [sendMessage, fingerprint]);

  // Loading state
  if (eventsLoading) {
    return (
      <section className="le-page">
        <div className="le-loading">
          <div className="le-loading-bar-track">
            <div className="le-loading-bar-fill" />
          </div>
        </div>
      </section>
    );
  }

  // Delegate to chunk-delete page
  if (event?.webAddon === 'chunk-delete') {
    return <ChunkDeletePage event={event} />;
  }

  // Delegate to would-you-rather page
  if (event?.webAddon === 'would-you-rather') {
    return <WouldYouRatherPage event={event} />;
  }

  // Delegate to block-delete page
  if (event?.webAddon === 'block-delete') {
    return <BlockDeletePage event={event} />;
  }

  // Event not found or no web addon
  if (!event || event.webAddon !== 'command-voting') {
    return (
      <section className="le-page">
        <div className="le-not-found">
          <h2>Event Not Found</h2>
          <p>This event doesn't exist or isn't currently live.</p>
        </div>
      </section>
    );
  }

  // Username gate
  if (!displayName) {
    return (
      <section className="le-page">
        <EventAuthBar />
        <UsernameGate onSubmit={setDisplayName} />
      </section>
    );
  }

  const isActive = roomState?.roundState === 'active';
  const inputPlaceholder = isActive
    ? '/command ...'
    : roomState?.roundState === 'idle'
      ? 'Waiting for the event to start...'
      : roomState?.roundState === 'paused'
        ? 'Event is paused...'
        : 'Next round starting soon...';

  return (
    <section className="le-page">
      <EventAuthBar />
      <div className="le-layout">
        {roomState?.allowedCommands?.length > 0 && (
          <AllowedCommandsSidebar commands={roomState.allowedCommands} />
        )}
        <div className="le-container">
          <h1 className="le-event-title" dangerouslySetInnerHTML={{ __html: renderMinecraftTextHTML(event.name || 'Live Event') }} />

          {wsError && <div className="le-ws-error">{wsError}</div>}
          {lastError && <div className="le-cmd-error">{lastError}</div>}

          {roomState ? (
            <>
              <RoundHeader state={roomState} />

              <CommandInput
                onSubmit={handleSubmitCommand}
                disabled={!isActive}
                placeholder={inputPlaceholder}
              />

              <CommandList
                commands={roomState.commands || []}
                yourVote={roomState.yourVote}
                onVote={handleVote}
                disabled={!isActive}
              />

              <RoundHistory history={roomState.history} />
            </>
          ) : (
            <div className="le-connecting">
              {turnstileError ? (
                <>
                  <span>Verification failed</span>
                  <button className="le-retry-btn" onClick={retryTurnstile}>Retry</button>
                </>
              ) : (
                <VerifyingProgress
                  done={!!sessionToken && !!roomState}
                  label={!sessionToken ? 'Verifying...' : 'Connecting to live server...'}
                />
              )}
            </div>
          )}

          <div className="le-user-bar">
            <span className="le-user-name">Playing as: {displayName}</span>
            {!isSignedIn && (
              <button
                className="le-change-name"
                onClick={() => {
                  localStorage.removeItem(DISPLAY_NAME_KEY);
                  setDisplayName('');
                }}
              >
                Change Name
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
